<script setup lang="ts">
import { type DurationFilterConfig } from '@itinerary/trip/TripModuleTypes';
import 'vue-slider-component/dist-css/vue-slider-component.css';
import 'vue-slider-component/theme/default.css';

const {
    min = {
        departure: 0,
        return: 0,
    },
    max = {
        departure: 0,
        return: 0,
    },
    roundTrip,
} = defineProps<IProps>();
const modelValue = defineModel<DurationFilterConfig<number[]>>({
    default: () => ({ departure: [0, 0], return: [0, 0] }),
});
const VueSlider = defineAsyncComponent(
    () =>
        import('vue-slider-component/dist-css/vue-slider-component.umd.min.js')
);
interface IProps {
    min?: DurationFilterConfig<number>;
    max?: DurationFilterConfig<number>;
    roundTrip?: boolean;
}
const departureValue_ = ref<number[]>();
const returnValue_ = ref<number[]>();
const departureDragging = ref(false);
const returnDragging = ref(false);
const formatter = (type: 'departure' | 'return') => (val: string) => {
    if (Number(val) === min[type] || Number(val) === max[type]) {
        return '';
    }
    return `${val} ساعت`;
};
const onChange = (type: 'departure' | 'return') => {
    if (
        (type === 'departure' && !departureDragging.value) ||
        (type === 'return' && !returnDragging.value)
    ) {
        modelValue.value = {
            departure: departureValue_.value || [],
            return: roundTrip ? returnValue_.value || [] : undefined,
        };
    }
};
const setDragging = (type: 'departure' | 'return', value: any) => {
    if (type === 'departure') {
        departureDragging.value = value;
    } else if (type === 'return') {
        returnDragging.value = value;
    }
};

const setValue_ = (type: 'departure' | 'return', value: any) => {
    if (type === 'departure') {
        departureValue_.value = value;
    } else if (type === 'return') {
        returnValue_.value = value;
    }
};
const clear = (type: 'departure' | 'return') => {
    setValue_(type, [min[type] || 0, max[type] || 0]);
};
const dragStart = (type: 'departure' | 'return') => {
    setDragging(type, true);
};
const dragEnd = (type: 'departure' | 'return') => {
    setDragging(type, false);
    modelValue.value = {
        departure: departureValue_.value || [],
        return: roundTrip ? returnValue_.value || [] : undefined,
    };
};
watch(
    modelValue,
    (val: DurationFilterConfig<number[]> | null) => {
        departureValue_.value =
            val && val.departure
                ? [
                      val.departure[0] >= min.departure
                          ? val.departure[0]
                          : min.departure,
                      val.departure[1] >= min.departure
                          ? val.departure[1]
                          : max.departure,
                  ]
                : [min.departure, max.departure];
        returnValue_.value =
            val && val.return
                ? [
                      val.return[0] >= (min.return ?? 0)
                          ? val.return[0] ?? 0
                          : min.return ?? 0,
                      val.return[1] >= (min.return ?? 0)
                          ? val.return[1] ?? 0
                          : max.return ?? 0,
                  ]
                : [min.return ?? 0, max.return ?? 0];
    },
    { immediate: true }
);

defineExpose({
    clear,
});
</script>

<template>
    <div class="duration-filter-container">
        <div class="duration-filter">
            <div v-if="roundTrip" class="subtitle">مدت زمان رفت</div>
            <div class="slider-wrapper">
                <ClientOnly>
                    <VueSlider
                        v-model="departureValue_"
                        :min="min.departure"
                        :max="max.departure"
                        tooltip="always"
                        tooltip-placement="top"
                        direction="rtl"
                        :tooltip-formatter="formatter('departure')"
                        @drag-end="dragEnd('departure')"
                        @drag-start="dragStart('departure')"
                        @change="onChange('departure')"
                    />
                </ClientOnly>
            </div>
            <div class="duration-wrapper">
                <mr-typography
                    weight="500"
                    color="var(--cool-gray-500)"
                    class="duration"
                    align="right"
                >
                    <span class="duration-value">{{ min.departure }} ساعت</span>
                </mr-typography>
                <mr-typography
                    weight="500"
                    color="var(--cool-gray-500)"
                    class="duration"
                    align="left"
                >
                    <span class="duration-value">{{ max.departure }} ساعت</span>
                </mr-typography>
            </div>
        </div>
        <div v-if="roundTrip" class="duration-filter">
            <div class="subtitle">مدت زمان برگشت</div>
            <div class="slider-wrapper">
                <ClientOnly>
                    <VueSlider
                        v-model="returnValue_"
                        :min="min.return"
                        :max="max.return"
                        tooltip="always"
                        tooltip-placement="top"
                        direction="rtl"
                        :tooltip-formatter="formatter('return')"
                        @drag-end="dragEnd('return')"
                        @drag-start="dragStart('return')"
                        @change="onChange('return')"
                    />
                </ClientOnly>
            </div>
            <div class="duration-wrapper">
                <mr-typography
                    weight="500"
                    color="var(--cool-gray-500)"
                    class="duration"
                >
                    <span class="duration-value">{{ min.return }} ساعت</span>
                </mr-typography>
                <mr-typography
                    weight="500"
                    color="var(--cool-gray-500)"
                    class="duration"
                >
                    <span class="duration-value">{{ max.return }} ساعت</span>
                </mr-typography>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.duration-filter-container {
    padding-top: 20px;
    .subtitle {
        @include tg-m($w5, usePalette($p-cool-gray, $p-500));
        margin-bottom: 12px;
    }
    .slider-wrapper {
        padding: 0 10px;
        :deep(.vue-slider-dot-handle) {
            width: 16px;
            height: 16px;
            @include shadow(0);
            border: 2px solid usePalette();
            background: white;
        }

        :deep(.vue-slider) {
            height: 3px !important;
        }

        :deep(.vue-slider-process) {
            background-color: usePalette();
        }

        :deep(.vue-slider-dot-tooltip-inner:after) {
            content: none;
        }

        :deep(.vue-slider-dot-tooltip-inner) {
            background: transparent;
            color: usePalette();
            font-size: 14px;
            @include font-weight(bold);
        }

        :deep(.vue-slider-dot-tooltip-bottom) {
            bottom: -5px;
        }
        :deep(.vue-slider-dot-tooltip-top) {
            top: 0;
        }
    }
    .duration-wrapper {
        display: flex;
        flex-direction: row;
        padding: 12.5px 0;
        .duration {
            padding: 7.5px 0;
            width: 50%;
        }
    }
}
</style>
